import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';

import { SubscriptionStatus } from '@utils/interfaces';

export interface User {
  billingAmountCBPT?: number;
  cbptTaxAmountExclusive?: number;
  cbptTotalWithTax?: number;
  dobVerified?: boolean;
  email?: string;
  id?: number;
  isDueInFuture?: boolean;
  isYPEligible?: boolean;
  name?: string;
  nextBillingDate?: string;
  // Lets a logged-out prefilled checkout (/subscribe?email=) create a paid subscription
  prefillToken?: string;
  stripeCustomerId?: string;
  subscriptionStatus?: SubscriptionStatus;
}

type SubscribeFormProviderProps = {
  children: ReactNode;
  // Whether the reader is in the wallet buttons (Express Checkout) arm of the Statsig experiment
  showExpressCheckout?: boolean;
  user: User;
};

const SubscribeFormContext = createContext<{
  user: User;
  setUser: Dispatch<SetStateAction<User>>;
  showExpressCheckout: boolean;
}>({
  user: {
    dobVerified: false,
    email: '',
    id: null,
    isYPEligible: false,
    name: '',
    stripeCustomerId: '',
    subscriptionStatus: null,
  },
  setUser: () => undefined,
  showExpressCheckout: false,
});

export const SubscribeFormProvider = ({
  children,
  showExpressCheckout = false,
  user: {
    billingAmountCBPT,
    cbptTaxAmountExclusive,
    cbptTotalWithTax,
    dobVerified,
    email,
    id,
    isDueInFuture,
    isYPEligible,
    name,
    nextBillingDate,
    prefillToken,
    stripeCustomerId,
    subscriptionStatus,
  },
}: SubscribeFormProviderProps) => {
  const [user, setUser] = useState<User>({
    billingAmountCBPT,
    cbptTaxAmountExclusive,
    cbptTotalWithTax,
    dobVerified,
    email,
    id,
    isDueInFuture,
    isYPEligible,
    name,
    nextBillingDate,
    prefillToken,
    stripeCustomerId,
    subscriptionStatus,
  });

  return (
    <SubscribeFormContext.Provider value={{ user, setUser, showExpressCheckout }}>
      {children}
    </SubscribeFormContext.Provider>
  );
};

export const useSubscribeForm = () => {
  const context = useContext(SubscribeFormContext);
  return context;
};
